import React, { useState, useEffect } from 'react';
import { getUsers, User, createUser, updateUser, deleteUser, UserFormData } from '../../api/usersApi';
import Layout from '../../components/Layout';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import TableHeader from '../../components/TableHeader';
import SearchFilter from '../../components/SearchFilter';
import { sortData, filterData, SortState } from '../../utils/tableUtils';
import Table, { TableColumn } from '../../components/Table';

// User form modal component
const UserFormModal: React.FC<{
  isOpen: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (data: UserFormData) => void;
  initialData?: User;
}> = ({ isOpen, title, onClose, onSubmit, initialData }) => {
  const [formData, setFormData] = useState<UserFormData>({
    first_name: '',
    last_name: '',
    username: '',
    password: '',
    is_admin: false
  });
  
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  
  // Reset form when modal opens/closes or initialData changes
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          first_name: initialData.first_name,
          last_name: initialData.last_name,
          username: initialData.username,
          password: '', // Don't populate password field for security
          is_admin: initialData.is_admin
        });
      } else {
        setFormData({
          first_name: '',
          last_name: '',
          username: '',
          password: '',
          is_admin: false
        });
      }
      setErrors({});
    }
  }, [isOpen, initialData]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };
  
  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    
    if (!formData.username.trim()) {
      newErrors.username = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.username)) {
      newErrors.username = 'Invalid email format';
    }
    
    if (!initialData && !formData.password?.trim()) {
      newErrors.password = 'Password is required for new users';
    } else if (formData.password?.trim() && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-10">
      <div className="bg-dark-100 rounded-lg w-full max-w-md p-6 shadow-lg">
        <h2 className="text-xl font-bold mb-4 text-white">{title}</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-300 mb-1" htmlFor="first_name">First Name</label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className={`w-full px-3 py-2 rounded bg-dark-200 border ${errors.first_name ? 'border-stake-red' : 'border-stake-border'} text-gray-300`}
            />
            {errors.first_name && <p className="text-stake-red text-sm mt-1">{errors.first_name}</p>}
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-300 mb-1" htmlFor="last_name">Last Name</label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className={`w-full px-3 py-2 rounded bg-dark-200 border ${errors.last_name ? 'border-stake-red' : 'border-stake-border'} text-gray-300`}
            />
            {errors.last_name && <p className="text-stake-red text-sm mt-1">{errors.last_name}</p>}
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-300 mb-1" htmlFor="username">Email</label>
            <input
              type="email"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className={`w-full px-3 py-2 rounded bg-dark-200 border ${errors.username ? 'border-stake-red' : 'border-stake-border'} text-gray-300`}
            />
            {errors.username && <p className="text-stake-red text-sm mt-1">{errors.username}</p>}
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-300 mb-1" htmlFor="password">
              Password {initialData && <span className="text-gray-400 text-sm">(Leave blank to keep unchanged)</span>}
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`w-full px-3 py-2 rounded bg-dark-200 border ${errors.password ? 'border-stake-red' : 'border-stake-border'} text-gray-300`}
            />
            {errors.password && <p className="text-stake-red text-sm mt-1">{errors.password}</p>}
          </div>
          
          <div className="mb-6">
            <label className="flex items-center text-gray-300">
              <input
                type="checkbox"
                name="is_admin"
                checked={formData.is_admin}
                onChange={handleChange}
                className="mr-2 accent-stake-blue"
              />
              Administrator
            </label>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-dark-300 hover:bg-dark-400 text-white rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-stake-blue hover:bg-blue-700 text-white rounded"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [displayedUsers, setDisplayedUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<User | undefined>(undefined);
  const [modalTitle, setModalTitle] = useState<string>('Add User');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortState, setSortState] = useState<SortState<User>>({
    key: 'username',
    direction: 'asc'
  });

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Apply sorting and filtering whenever the base data, search term, or sort state changes
  useEffect(() => {
    let filteredData = users;
    
    // Apply search filtering
    if (searchTerm) {
      filteredData = filterData(
        users, 
        searchTerm, 
        ['first_name', 'last_name', 'username']
      );
    }
    
    // Apply sorting
    filteredData = sortData(filteredData, sortState.key, sortState.direction);
    
    setDisplayedUsers(filteredData);
  }, [users, searchTerm, sortState]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchUsers = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const fetchedUsers = await getUsers();
      setUsers(fetchedUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sort column click
  const handleSort = (key: keyof User) => {
    setSortState(prevState => ({
      key,
      direction: prevState.key === key && prevState.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Open modal for creating a new user
  const handleAddUser = (): void => {
    setCurrentUser(undefined);
    setModalTitle('Add User');
    setIsModalOpen(true);
  };

  // Open modal for editing an existing user
  const handleEditUser = (user: User): void => {
    setCurrentUser(user);
    setModalTitle('Edit User');
    setIsModalOpen(true);
  };

  // Open delete confirmation for a user
  const handleDeleteClick = (userId: number): void => {
    setUserToDelete(userId);
    setShowDeleteConfirm(true);
  };
  
  // Handle user deletion
  const handleConfirmDelete = async (): Promise<void> => {
    if (!userToDelete) return;
    
    try {
      await deleteUser(userToDelete);
      setUsers(users.filter(u => u.id !== userToDelete));
      setSuccessMessage('User deleted successfully');
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user. Please try again.');
    } finally {
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const handleSubmitUser = async (userData: UserFormData): Promise<void> => {
    try {
      let savedUser: User;
      
      if (currentUser) {
        // Update existing user
        savedUser = await updateUser(currentUser.id, userData);
        setUsers(users.map(u => u.id === savedUser.id ? savedUser : u));
        setSuccessMessage('User updated successfully');
      } else {
        // Create new user
        savedUser = await createUser(userData);
        setUsers([...users, savedUser]);
        setSuccessMessage('User created successfully');
      }
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving user:', err);
      setError('Failed to save user. Please try again.');
    }
  };

  // Define table columns
  const columns: TableColumn<User>[] = [
    {
      key: 'id',
      title: 'ID',
      render: (user) => <span className="text-gray-400">{user.id}</span>,
      sortable: true
    },
    {
      key: 'first_name',
      title: 'First Name',
      render: (user) => <span className="text-white">{user.first_name}</span>,
      sortable: true
    },
    {
      key: 'last_name',
      title: 'Last Name',
      render: (user) => <span className="text-white">{user.last_name}</span>,
      sortable: true
    },
    {
      key: 'username',
      title: 'Email',
      render: (user) => <span className="text-gray-300">{user.username}</span>,
      sortable: true
    },
    {
      key: 'is_admin',
      title: 'Admin',
      render: (user) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${user.is_admin ? 'bg-stake-blue text-white' : 'bg-dark-300 text-gray-300'}`}>
          {user.is_admin ? 'Yes' : 'No'}
        </span>
      ),
      sortable: true
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (user) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEditUser(user)}
            className="text-stake-blue hover:text-blue-300 p-1 rounded hover:bg-dark-200 transition-colors"
            title="Edit User"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleDeleteClick(user.id)}
            className="text-stake-red hover:text-red-400 p-1 rounded hover:bg-dark-200 transition-colors"
            title="Delete User"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  return (
    <Layout>
      <div className="-mx-3 sm:mx-0 sm:px-4 py-4">
        <div className="flex justify-between items-center mb-6 px-3 sm:px-0">
          <button
            onClick={handleAddUser}
            className="flex items-center bg-stake-blue hover:bg-blue-700 text-white px-4 py-2 rounded"
          >
            <PlusIcon className="h-5 w-5 mr-1" /> Add User
          </button>
        </div>
        
        {/* Error and success messages */}
        {error && (
          <div className="mb-4 p-3 bg-stake-red text-white rounded">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mb-4 p-3 bg-stake-green text-white rounded">
            {successMessage}
          </div>
        )}
        
        {/* Search filter */}
        <div className="mb-4">
          <SearchFilter
            placeholder="Search users..."
            value={searchTerm}
            onChange={setSearchTerm}
          />
        </div>
        
        {/* Users table */}
        <Table<User>
          fullWidth
          compact
          data={displayedUsers}
          columns={columns}
          keyExtractor={(user) => user.id}
          emptyMessage="No users found"
          isLoading={isLoading}
          sortState={sortState}
          onSort={handleSort}
          searchTerm={searchTerm}
        />
        
        {/* User form modal */}
        {isModalOpen && (
          <UserFormModal
            isOpen={isModalOpen}
            title={modalTitle}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleSubmitUser}
            initialData={currentUser}
          />
        )}
        
        {/* Delete confirmation modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-10">
            <div className="bg-dark-100 rounded-lg p-6 max-w-md w-full">
              <h3 className="text-xl font-bold mb-4 text-white">Confirm Delete</h3>
              <p className="mb-6 text-gray-300">Are you sure you want to delete this user? This action cannot be undone.</p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-dark-300 hover:bg-dark-400 text-white rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-stake-red hover:bg-red-700 text-white rounded"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Users; 